import { mutationOptions, useQueryClient } from '@tanstack/react-query';

import {
    NOTES_SETTINGS_TYPE,
    TELEHEALTH_SETTINGS_TYPE,
} from '../model/practice-member-settings.schemas';
import type {
    NotesSettingsPatch,
    TelehealthSettingsValue,
} from '../model/practice-member-settings.types';
import { practiceMemberSettingsApi } from './practice-member-settings.api';
import {
    notesSettingsOptions,
    telehealthSettingsOptions,
} from './practice-member-settings.queries';

// Cached on success rather than optimistically in onMutate: there is no rollback on failure, so
// an optimistic write would leave the cache showing a change that never reached the server.
export function useUpdateNotesSettingsOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (patch: NotesSettingsPatch) =>
            practiceMemberSettingsApi.updateByType(NOTES_SETTINGS_TYPE, patch),
        onSuccess: async (_, patch) => {
            await queryClient.cancelQueries({ queryKey: notesSettingsOptions().queryKey });
            queryClient.setQueryData(notesSettingsOptions().queryKey, (previous) => {
                if (!previous) return previous;
                return { type: NOTES_SETTINGS_TYPE, value: { ...previous.value, ...patch } };
            });
        },
    });
}

// `value` is always the complete shape — the server merge the notes mutation relies on isn't in
// play here, since every write already carries all three fields (`linkType`, `link`,
// `instructions`). Cached on success for the same reason as above: no rollback on failure.
export function useUpdateTelehealthSettingsOptions() {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationFn: (value: TelehealthSettingsValue) =>
            practiceMemberSettingsApi.updateByType(TELEHEALTH_SETTINGS_TYPE, value),
        onSuccess: async (_, value) => {
            await queryClient.cancelQueries({ queryKey: telehealthSettingsOptions().queryKey });
            queryClient.setQueryData(telehealthSettingsOptions().queryKey, (previous) => {
                if (!previous) return previous;
                return { type: TELEHEALTH_SETTINGS_TYPE, value: { ...previous.value, ...value } };
            });
        },
    });
}
